import React, { forwardRef, ForwardedRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  useBasketStore,
  useGlobalStore,
  useOrderStore,
  useProductStore,
} from '../../../store/hooks/useStore'
import { Box, Flex, Stack, Text, Button } from '../../../vanilla'
import { ChevronDownIcon, ExclamationTriangleIcon } from '../../../components/icons'
import { OrderSummary } from '../../../components/order-summary'
import { OrderSummaryProgressBars } from '../../../components/order-summary/progress-bars'
import { CartItem } from '../../../components/cart-item'
import { useExitEditModeModal } from '../use-exit-order-edit-mode-context'
import { OrderEditRemovedCartItem } from './order-edit-removed-cart-item'
import { CartSubstitutesOption } from './cart-substitutes-option'
import { useItemsUnavailable } from '../../../store/hooks/useItemsUnavailable'
import { PreviouslyRemovedItem } from '../../../types/store/basket'
import { useBeginCheckoutTracking } from '../../../analytics/click-event-tracking'
import { FreeGift } from './free-gift'
import { BasketToCheckoutButton } from './basket-to-checkout-button'
import { ProductItem } from '../../../store/BasketStore'
import { AvailablePaymentMethodsIcons } from '../../../components/credit-card-type-icon'
import LazyLoad from 'react-lazyload'

interface ShoppingBasketProps {
  checkoutButtonText?: string
  handleCheckoutButtonClick: () => void
  includeBag?: boolean
  setIncludeBag?: (value: boolean) => void
}

const ShoppingBasketContainer = forwardRef<HTMLDivElement, ShoppingBasketProps>(
  (
    { checkoutButtonText, handleCheckoutButtonClick, includeBag, setIncludeBag }: ShoppingBasketProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const exitEditModeModal = useExitEditModeModal()

    const { minimumBasketValue, basket, groupedBonusItems  }= useBasketStore()
    const { editMode, orderEditProductItemsDelta, orderEditRemovedProducts } = useOrderStore()
    const { customSitePreferences, availablePaymentMethods } = useGlobalStore()
    const { SubstituteToggleEnabled } = customSitePreferences

    const someItemsUnavailable = useItemsUnavailable()

    const { productsById } = useProductStore()

    const sendBeginCheckout = useBeginCheckoutTracking(productsById)

    const handleClick = () => {
      handleCheckoutButtonClick()
      sendBeginCheckout(basket, basket?.productItems)
    }

    const showEditOrderMessage =
      editMode &&
      (Object.values(orderEditProductItemsDelta).find(({ recentlyAdded }) => recentlyAdded) || false)

    return (
      <Stack spacing="0px" ref={ref}>
        <Flex
          width="full"
          display={['flex', 'none']}
          align="center"
          direction="column"
          marginY="12px"
          gap="8px"
        >
          <Text variant="text1" align="center" lineHeight="none">
            See your products
          </Text>
          <ChevronDownIcon boxSize="12px" />
        </Flex>
        {(basket?.productTotal || 0) < (minimumBasketValue || 0) && (
          <Flex
            style={{ backgroundColor: '#f9e5e9' }}
            paddingX="12px"
            paddingY="16px"
            gap="12px"
            marginY={['0px', '8px']}
            align="center"
          >
            <ExclamationTriangleIcon color="accent0" boxSize="32px" />
            <Text variant="text3" color="accent0" data-test-selector="minimum-spend-message">
              Minimum spend of £{minimumBasketValue}
            </Text>
          </Flex>
        )}

        <Flex
          borderBottom="1px"
          borderColor="gray200"
          bg="white"
          justifyContent="space-between"
          flexDirection={['column', 'row']}
          alignItems="stretch"
        >
          <Text padding="16px" variant="heading1">
            Your Shopping Basket
          </Text>
          {SubstituteToggleEnabled === true && (
            <CartSubstitutesOption togglerId="substitutes-option-toggler-1" />
          )}
        </Flex>
        {someItemsUnavailable ? (
          <Stack spacing="0px" bg="white">
            <Box style={{ backgroundColor: '#fbf7f8' }} padding="16px" margin="4px">
              <Text color="accent0">
                Some items in your basket are unavailable. These will be automatically removed when
                you checkout.
              </Text>
            </Box>

            {basket?.c_previouslyRemovedItems.map((removedItem: PreviouslyRemovedItem) =>
              removedItem.pid ? (
                <CartItem key={removedItem.pid} id={removedItem.pid} isUnavailable={true} />
              ) : null,
            )}
          </Stack>
        ) : null}
        {basket?.productItems?.length ? (
          <Stack spacing="0px" bg="white" data-test-selector="shopping-product-list">
             {groupedBonusItems.map((productItem: ProductItem) =>
              productItem.productId ? (
                <FreeGift
                  inDrawer={false}
                  key={productItem.productId}
                  id={productItem.productId}
                  name={productItem.productName}
                  quantity={productItem.quantity}
                />
              ) : null,
            )}

            {basket.productItems.map((productItem) =>
              productItem.productId ? (
                <CartItem key={productItem.itemId} id={productItem.productId} />
              ) : null,
            )}
            {editMode &&
              orderEditRemovedProducts.map((orderProduct) => {
                return (
                  <OrderEditRemovedCartItem
                    key={orderProduct.productId}
                    productId={orderProduct.productId || ''}
                  />
                )
              })}
          </Stack>
        ) : null}
        {SubstituteToggleEnabled === true && (
          <CartSubstitutesOption isBottomBorder togglerId="substitutes-option-toggler-2" />
        )}
        <OrderSummary
          tabledMode
          showItemsCount
          bg="white"
          display={['none', 'flex']}
          minWidthItemsAmount={280}
          rowsContainerProps={{
            paddingRight: '100px',
          }}
          includeBag={includeBag}
          setIncludeBag={setIncludeBag}
        />
        <Flex padding="16px" display={['none', 'flex']} bg="white">
          <Stack width={'half'}>
            <LazyLoad>
              <Flex direction='row' flexWrap='wrap' gap={['8px', '16px']} >
                <AvailablePaymentMethodsIcons availablePaymentMethods={availablePaymentMethods} width={'60px'} height={'30px'} />
              </Flex>
            </LazyLoad>
          </Stack>

          <Stack width="half" align="flex-end" paddingLeft="24px">
            {!editMode && (
              <OrderSummaryProgressBars border={false}/>
            )}
            {showEditOrderMessage && (
              <Box marginBottom="8px" width="full">
                <Text variant="text1" color="promotionColorDark" align="center">
                  You are editing an existing order, changes will only take effect once you
                  checkout.
                </Text>
              </Box>
            )}
            <Flex
              direction={['column', 'row']}
              align="center"
              gap={['12px', '24px']}
              width="full"
              justify={editMode ? 'flex-start' : 'flex-end'}
            >
              {editMode && (
                <Button
                  width="half"
                  data-test-selector="discard-changes-button"
                  variant="outlineDark"
                  size="sm"
                  onClick={() => {
                    exitEditModeModal?.onOpen()
                  }}
                >
                  Discard Changes and Exit
                </Button>
              )}
              <BasketToCheckoutButton 
                width='half'
                data-test-selector="checkout-securely-button"
                checkoutButtonText={checkoutButtonText ?? ''} 
                onClick={() => handleClick()}
              />
            </Flex>
          </Stack>
        </Flex>
      </Stack>
    )
  },
)

export const ShoppingBasket = observer(ShoppingBasketContainer)
